import {Matrix4,Vector2,Vector3,Vector4} from 'three';

type Raster={width:number;height:number;data:ArrayLike<number>};

/** Rectify one real floor sample into room metres. The repeated texture only
 * translates: reflecting either axis reverses diagonal plank/grain directions.
 * The entire floor uses this map, rather than splicing a photo-shaped footprint
 * (which can contain a doorway or a third wall) into the repeated sample. */
export function rectifyRoomFloorPatch(image:Raster,plane:Vector3[],projection:Matrix4,patch:Vector4,resolution=512){
 const origin=plane[0],u=plane[1].clone().sub(origin).normalize(),v=plane[3].clone().sub(origin).normalize();
 const period=new Vector2(patch.z,patch.w);
 const width=2**Math.max(1,Math.ceil(Math.log2(resolution*period.x/Math.max(period.x,period.y))));
 const height=2**Math.max(1,Math.ceil(Math.log2(resolution*period.y/Math.max(period.x,period.y))));
 const data=new Uint8Array(width*height*4),world=new Vector3();
 const sample=(a:number,b:number,c:number)=>{
  world.copy(origin).addScaledVector(u,patch.x+a).addScaledVector(v,patch.y+b).applyMatrix4(projection);
  const x=Math.max(0,Math.min(image.width-1,(world.x*.5+.5)*image.width-.5));
  const y=Math.max(0,Math.min(image.height-1,(.5-world.y*.5)*image.height-.5));
  const x0=Math.floor(x),y0=Math.floor(y),x1=Math.min(x0+1,image.width-1),y1=Math.min(y0+1,image.height-1),fx=x-x0,fy=y-y0;
  const at=(px:number,py:number)=>image.data[(py*image.width+px)*4+c];
  return (at(x0,y0)*(1-fx)+at(x1,y0)*fx)*(1-fy)+(at(x0,y1)*(1-fx)+at(x1,y1)*fx)*fy;
 };
 for(let y=0;y<height;y++)for(let x=0;x<width;x++){
  const a=(x+.5)/width*period.x,b=(y+.5)/height*period.y;
  for(let c=0;c<3;c++)data[(y*width+x)*4+c]=Math.round(sample(a,b,c));
  data[(y*width+x)*4+3]=255;
 }
 // DataTexture starts at UV (0,0); rows follow the floor's +V, not canvas Y.
 return {data,width,height,period};
}

function fft(real:Float64Array,imag:Float64Array,inverse:boolean){
 const n=real.length;
 for(let i=1,j=0;i<n;i++){let bit=n>>1;for(;j&bit;bit>>=1)j^=bit;j^=bit;if(i<j){[real[i],real[j]]=[real[j],real[i]];[imag[i],imag[j]]=[imag[j],imag[i]];}}
 for(let length=2;length<=n;length*=2){
  const angle=(inverse?2:-2)*Math.PI/length,cr=Math.cos(angle),ci=Math.sin(angle);
  for(let start=0;start<n;start+=length){let wr=1,wi=0;for(let k=0;k<length/2;k++){
   const a=start+k,b=a+length/2,vr=real[b]*wr-imag[b]*wi,vi=real[b]*wi+imag[b]*wr;
   real[b]=real[a]-vr;imag[b]=imag[a]-vi;real[a]+=vr;imag[a]+=vi;
   const next=wr*cr-wi*ci;wi=wr*ci+wi*cr;wr=next;
  }}
 }
 if(inverse)for(let i=0;i<n;i++){real[i]/=n;imag[i]/=n;}
}
function fft2(real:Float64Array,imag:Float64Array,width:number,height:number,inverse:boolean){
 for(let y=0;y<height;y++)fft(real.subarray(y*width,(y+1)*width),imag.subarray(y*width,(y+1)*width),inverse);
 const columnR=new Float64Array(height),columnI=new Float64Array(height);
 for(let x=0;x<width;x++){
  for(let y=0;y<height;y++){columnR[y]=real[y*width+x];columnI[y]=imag[y*width+x];}
  fft(columnR,columnI,inverse);
  for(let y=0;y<height;y++){real[y*width+x]=columnR[y];imag[y*width+x]=columnI[y];}
 }
}
/** Remove only the smooth component needed to close the tile's boundary.
 * Solving the boundary's periodic Poisson equation avoids mirroring, shifted
 * board crossfades, or copying edge details into the interior of the floor. */
export function closeFloorTextureEdges(image:Raster){
 const {width,height,data}=image,result=new Uint8Array(width*height*4);
 const at=(x:number,y:number,c:number)=>data[(y*width+x)*4+c];
 for(let c=0;c<3;c++){
  const real=new Float64Array(width*height),imag=new Float64Array(width*height);
  for(let x=0;x<width;x++){const delta=at(x,height-1,c)-at(x,0,c);real[x]=delta;real[(height-1)*width+x]=-delta;}
  for(let y=0;y<height;y++){const delta=at(width-1,y,c)-at(0,y,c);real[y*width]+=delta;real[y*width+width-1]-=delta;}
  fft2(real,imag,width,height,false);
  for(let y=0;y<height;y++)for(let x=0;x<width;x++){
   const i=y*width+x,denominator=2*Math.cos(2*Math.PI*x/width)+2*Math.cos(2*Math.PI*y/height)-4;
   if(i===0){real[i]=0;imag[i]=0;}else{real[i]/=denominator;imag[i]/=denominator;}
  }
  fft2(real,imag,width,height,true);
  for(let i=0;i<width*height;i++)result[i*4+c]=Math.max(0,Math.min(255,Math.round(data[i*4+c]-real[i])));
 }
 for(let i=0;i<width*height;i++)result[i*4+3]=255;
 return result;
}

export function buildRoomFloorTexture(image:Raster,plane:Vector3[],projection:Matrix4,patch:Vector4,resolution=512){
 const tile=rectifyRoomFloorPatch(image,plane,projection,patch,resolution);
 return {...tile,data:closeFloorTextureEdges(tile)};
}
